import { signUp } from "@d12labs/auth/client";
import { apiFetch } from "@d12labs/client";
import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AuthLayout } from "@/components/pages/auth-layout";
// @scaffold:oauth start
import { OauthButtons } from "@/components/pages/oauth-buttons";
// @scaffold:oauth end

export function SignupPage() {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const navigate = useNavigate();
	const { data: authConfig } = useQuery({
		queryKey: ["account", "providers"],
		queryFn: () => apiFetch<{ signup?: boolean }>("/api/account/providers"),
		staleTime: Infinity,
	});

	useEffect(() => {
		if (authConfig && authConfig.signup !== true) {
			void navigate({ to: "/login", replace: true });
		}
	}, [authConfig, navigate]);

	if (authConfig && authConfig.signup !== true) {
		return null;
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		const { error: signUpError } = await signUp.email({ name, email, password });
		setSubmitting(false);
		if (signUpError) {
			setError(signUpError.message ?? "Sign up failed");
			return;
		}
		await navigate({ to: "/home" });
	};

	return (
		<AuthLayout
			title="Create account"
			description="One minute, then you're in."
			footer={
				<>
					Already have an account?{" "}
					<Link to="/login" className="underline">
						Sign in
					</Link>
				</>
			}
		>
			{/* @scaffold:oauth start */}
			<OauthButtons callbackURL="/home" />
			{/* @scaffold:oauth end */}
			<form onSubmit={handleSubmit} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor="name">Name</Label>
					<Input
						id="name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						required
						autoComplete="name"
						autoFocus
					/>
				</div>
				<div className="flex flex-col gap-2">
					<Label htmlFor="email">Email</Label>
					<Input
						id="email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						required
						autoComplete="email"
					/>
				</div>
				<div className="flex flex-col gap-2">
					<Label htmlFor="password">Password</Label>
					<Input
						id="password"
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						required
						autoComplete="new-password"
						minLength={8}
					/>
					<p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<Button type="submit" disabled={submitting}>
					{submitting ? "Creating…" : "Create account"}
				</Button>
			</form>
		</AuthLayout>
	);
}
